import { useEffect, useState } from "react";
import { api, ApiError, debugMode, lastTrace,
         type DiscoverResponse, type GscSample, type Ga4Sample,
         type Property, type SourceRef } from "../lib/api";
import { DatasetPicker } from "../components/DatasetPicker";
import { GbpPanel } from "../components/GbpPanel";
import { DebugPanel } from "../components/DebugPanel";

type Phase = "connect" | "discovering" | "choosing" | "naming";

/**
 * Adds or re-points one client. Discovery runs against the connected Google
 * account, the user picks datasets by their real content, then names the
 * client. Naming last, because the samples are what tell you whose data it is.
 */
export function Setup({ connectionId, workspaceId, editing, onCancel, onSaved, onOpenGuide }: {
  connectionId: string | null;
  workspaceId: string;
  editing?: Property | null;
  onCancel?: () => void;
  onSaved: (propertyId: string) => void;
  onOpenGuide?: () => void;
}) {
  const [phase, setPhase] = useState<Phase>(connectionId ? "discovering" : "connect");
  const [found, setFound] = useState<DiscoverResponse | null>(null);
  const [picked, setPicked] = useState<{ gsc: GscSample | null; ga4: Ga4Sample | null } | null>(null);
  const [name, setName] = useState(editing?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  function fail(e: unknown, fallback: string) {
    const err = e as ApiError;
    setFailure(err.body);
    setError(err.hint ?? `${fallback} (${err.status}${err.code ? ` ${err.code}` : ""})`);
  }

  useEffect(() => {
    if (!connectionId) { setPhase("connect"); return; }
    setPhase("discovering");
    api.discover(connectionId)
      .then((d) => { setFound(d); setPhase("choosing"); })
      .catch((e) => { fail(e, "Could not read your BigQuery projects."); setPhase("choosing"); });
  }, [connectionId]);

  function choose(sel: { gsc: GscSample | null; ga4: Ga4Sample | null }) {
    setPicked(sel);
    // A sensible default name beats an empty box: the site the export covers.
    if (!name) {
      const host = sel.gsc?.siteUrl?.replace(/^sc-domain:|^https?:\/\/|\/$/g, "")
        ?? sel.ga4?.topHosts[0] ?? "";
      setName(host);
    }
    setPhase("naming");
  }

  async function save() {
    if (!connectionId || !picked) return;
    setBusy(true); setError(null);
    const toRef = (s: GscSample | Ga4Sample | null): SourceRef | null => s && {
      project: s.project, dataset: s.dataset, location: s.location,
      siteUrl: "siteUrl" in s ? s.siteUrl : null,
    };
    try {
      const { property_id } = await api.saveProperty({
        workspace_id: workspaceId,
        connection_id: connectionId,
        property_id: editing?.id,
        name: name.trim() || "Untitled client",
        site_domain: picked.gsc?.siteUrl ?? null,
        gsc: toRef(picked.gsc),
        ga4: toRef(picked.ga4),
      });
      onSaved(property_id);
    } catch (e) {
      fail(e, "Could not save this client.");
    } finally { setBusy(false); }
  }

  if (phase === "connect") {
    return (
      <div className="shell">
        <h1>Connect Google</h1>
        <p>
          Read access to Search Console and GA4. BigQuery exports are found
          automatically and unlock the deeper analyses.
        </p>
        <ConnectButton workspaceId={workspaceId} onError={fail} />
        {error && <div className="warning" style={{ marginTop: ".75rem" }}>{error}</div>}
        {onCancel && <button className="quiet" onClick={onCancel}>Cancel</button>}
        <Debug failure={failure} />
      </div>
    );
  }

  if (phase === "discovering") {
    return (
      <div className="shell">
        <h1>Finding your data</h1>
        <p className="muted">
          Reading every BigQuery project this account can see, and pulling real
          pages out of each export so you can tell them apart.
        </p>
      </div>
    );
  }

  if (phase === "naming" && picked) {
    return (
      <div className="shell">
        <h1>{editing ? "Rename this client" : "Name this client"}</h1>
        <p className="muted">
          How it appears in your client switcher. Only you see it.
        </p>
        <div className="panel" style={{ maxWidth: 460 }}>
          <div className="field">
            <label htmlFor="cname">Client name</label>
            <input id="cname" value={name} autoFocus
                   onChange={(e) => setName(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") void save(); }} />
          </div>
          <p className="caveat">
            {picked.gsc
              ? `Search Console: ${picked.gsc.dataset}`
              : "No Search Console export selected."}
            {picked.ga4 ? ` · GA4: ${picked.ga4.dataset}` : ""}
          </p>
          <button onClick={save} disabled={busy || !name.trim()}>
            {busy ? "Saving" : editing ? "Save changes" : "Add client"}
          </button>{" "}
          <button className="quiet" onClick={() => setPhase("choosing")}>Back</button>
        </div>
        {error && <div className="warning">{error}</div>}
        <Debug failure={failure} />
      </div>
    );
  }

  return (
    <div className="shell">
      <h1>{editing ? `Sources for ${editing.name}` : "Pick your sources"}</h1>
      {error && <div className="warning">{error}</div>}

      {found && (
        <>
          {found.warnings.map((w, i) => <div className="warning" key={i}>{w}</div>)}
          <DatasetPicker gsc={found.gsc} ga4={found.ga4} busy={busy} onConfirm={choose} />
          <details style={{ marginTop: ".9rem" }}>
            <summary className="muted">
              Scanned {found.projects.filter((p) => p.scanned).length} of{" "}
              {found.projects.length} projects.
              {found.gsc.length === 0 && " Expected an export here? Open this."}
            </summary>
            <ul className="proj-list">
              {found.projects.map((p) => (
                <li key={p.id}>
                  <span className="num">{p.id}</span>
                  <span className="muted">
                    {p.status === "ok" && `${p.datasetCount} datasets`}
                    {p.status === "no_bigquery" && "BigQuery not enabled"}
                    {p.status === "error" && (p.detail ?? "unreadable")}
                    {p.status === "skipped" && "not scanned"}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        </>
      )}

      {editing && (
        <GbpPanel
          propertyId={editing.id}
          connectionId={connectionId}
          onOpenGuide={() => onOpenGuide?.()}
        />
      )}

      {onCancel && (
        <p style={{ marginTop: "1rem" }}>
          <button className="quiet" onClick={onCancel}>Cancel</button>
        </p>
      )}
      <Debug failure={failure} />
    </div>
  );
}

function ConnectButton({ workspaceId, onError }: {
  workspaceId: string;
  onError: (e: unknown, fallback: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const { url } = await api.authUrl(workspaceId);
          window.location.href = url;
        } catch (e) {
          onError(e, "Could not start Google sign-in.");
          setBusy(false);
        }
      }}
    >
      {busy ? "Opening Google" : "Connect Google account"}
    </button>
  );
}

function Debug({ failure }: { failure: unknown }) {
  if (!debugMode() && failure === null) return null;
  return <DebugPanel trace={lastTrace} error={failure ?? undefined} />;
}
